import { Component, Input, OnDestroy, SimpleChanges, OnChanges, ViewChild, OnInit, AfterViewInit, ViewEncapsulation, ElementRef, ViewChildren, QueryList, ChangeDetectorRef, Output, EventEmitter, ViewContainerRef } from '@angular/core';
import { Subject } from 'rxjs';
import { LightShowTrackService } from '../_services/track.service';
import { Mp3PlayerComponent, SegmentSelectedData, Segment } from '../mp3-slider/mp3-player.component';
import { PhotoService } from '../_services/photo.service';
import { FormGroup, FormBuilder, FormArray, Validators, ValidationErrors, AbstractControl, ValidatorFn, FormControl } from '@angular/forms';
import { SoundEffectService, SoundEffect } from '../_services/sound-effect.service';
import { FormHelpers } from '../_shared/form-helpers';
import { LightShowTypeCodes } from '../_models/LightShowTypeCodes';
import { LightShowOperation } from './Operations/lightshow-operation';
import { LightshowState, EditorMode, SegmentMode } from './lightshow-state';
import { LightShowSegment } from './Segments/lightshow-segment';
import { AuthService } from '../_services/auth.service';

import * as moment from 'moment';
import { CustomValidators } from '../_shared/custom-validators';
import { LightshowSegmentTypes } from './Segments/lightshow-segment-types';
import { AudioSegment } from './Segments/audio-segment';
import { ImageSegment } from './Segments/image-segment';
import { ToggleSmartColorsSegment } from './Segments/toggle-smart-colors-segment';
import { ToggleSolidColorSegment } from './Segments/toggle-solid-color-segment';
import { StrobeOperation } from './Operations/strobe-operation';
import { TorchOperation } from './Operations/torch-operation';
import { takeUntil, debounceTime, take } from 'rxjs/operators';
import { ErrorTooltipComponent } from './Components/error-tooltip.component';
import { FlashTypes } from './operation-types';
import { PlayAudioOperation } from './Operations/play-audio-operation';
import { ImageFadeinnOperation } from './Operations/image-fadein-operation';
import { LightShowService, LightShowServiceFileCollection } from '../_services/light-show.service';
import { LightShowTrack } from '../_models/LightShowTrack';
import { VibrateOperation } from './Operations/vibrate-operation';
import { VibrateSegment } from './Segments/vibrate-segment';
import { SparkleOperation } from './Operations/sparkle-operation';
import { VenueService } from '../_services/venue.service';
import { Venue, Sequence } from '../_models/Venue';
import { VenueSection } from '../_models/VenueSection';
import { DialogService } from '../_services/dialog.service';
import { LightshowTorchEditorComponent } from './lightshow-torch-editor/lightshow-torch-editor.component';
import { ScreenStrobeSegment } from './Segments/screen-strobe-segment';
import { BlankSegment } from './Segments/blank-segment';
import { Engine } from '../_models/Engine';
import { restrictImageSegmentStartTime } from '../_shared/utils';

export enum LightShowDefinitionMode {
  Standard = 0,
  Custom = 1
}

export enum SegmentType {
  SolidColor,
  SmartColor,
  ScreenStrobe,
  SoundEffect,
  Image,
  Vibrate,
  Unknown
}

const VIBRATE_SEGMENT_URL = 'https://lightshow-editor-sound-effects.s3.amazonaws.com/vibrate-iphone6.mp3';

export interface LightShowDefinition {
  cropEndPercent: number;
  mode: LightShowDefinitionMode;
  customModeData: { segments: LightShowOperation[] };
  lightShowJson: string;
}

export interface LibraryImage {
  name: string;
  url: any; // SafeUrlImpl | string
  location: string;
  stage: number;
  imageBackgroundColor?: string;
  size: number;
}

@Component({
  selector: 'app-lightshow-preview',
  templateUrl: './lightshow-preview.component.html',
  styleUrls: ['./lightshow-preview.component.css', '../venue-editor/form/venue-form.component.css'],
  encapsulation: ViewEncapsulation.None
})

export class LightshowPreviewComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input()
  public set track(track: LightShowTrack) {

    if (track) {

      this._track = track;

      // If we uploaded a custom track clean up the segments too
      if (track?.trackId === -1) {
        setTimeout(() => this.cleanSegments(), 0);
      }

      this.loadTrackData();
    }
  }
  @Input()
  public set lightShowTypeCode(type: LightShowTypeCodes) {
    this._lightShowTypeCode = type;

    if (type === LightShowTypeCodes.libraryTrack) {
      this.changeEditorMode(EditorMode.SCREEN);
    }
  }

  public minFadeIn = 200;

  public selectedVenueIdValue = 0;

  @Input()
  venueId: number;

  @Input()
  hasSiteManagement: boolean;

  @Input()
  public enableVenueEdit: boolean;

  @Input()
  public serviceFiles: LightShowServiceFileCollection;

  @Output()
  public selectedVenue: EventEmitter<Venue> = new EventEmitter<Venue>();

  public venue: Venue;
  public selectedGroup = '';
  public selectedSequence: Sequence;

  @Output()
  saveLibTrackChanges = new EventEmitter<boolean>();

  public toggleSaveLibTrackChanges($event) {
    this.saveLibTrackChanges.emit($event);
  }

  public newGroupName = '';

  public userVenues: Venue[] = [];

  // TODO: rename to selectedSegmentFG so it's clear it's a FormGroup
  public get selectedSegment(): FormGroup {
    return (this.form.get('customModeData.segments') as FormArray).controls[this.form.get('customModeData.selectedSegmentIndex').value] as FormGroup;
  }

  get preview() {
    return this.previewRef.nativeElement;
  }

  public get lightShowTypeCode() {
    return this._lightShowTypeCode;
  }

  public get track() {
    return this._track;
  }

  public set selectedSegment(segGroup: FormGroup) {
    this.form.markAsDirty();
    const idx = (this.form.get('customModeData.segments') as FormArray).controls.indexOf(segGroup);
    this.form.get('customModeData').patchValue({ selectedSegmentIndex: idx });
  }

  public get imageLibrary(): LibraryImage[] {
    return this.form.get('customModeData.imageLibrary').value;
  }

  public get colorsLibrary() {
    return this.form.get('customModeData.colorsLibrary').value;
  }

  public get selectedSegmentValue(): LightShowSegment {
    if (this.selectedSegment) {
      const val = this.selectedSegment.value;
      LightshowPreviewComponent.initSegment(val);
      return val;
    }
  }
  // TODO: rename to segmentsFA
  public get segments(): LightShowSegment[] {
    return this.form.get('customModeData.segments').value;
  }

  public get dropZone() {
    if (this.dropZoneRef) {
      return this.dropZoneRef.nativeElement as HTMLDivElement;
    }
  }

  public get showFullScreenPreview() {
    return this.lightshowService.showFullScreenPreview;
  }

  private get cropEndPercent() {
    return this.form.get('cropEndPercent').value;
  }

  constructor(
    private trackService: LightShowTrackService,
    private photoService: PhotoService,
    private authservice: AuthService,
    private soundEffectService: SoundEffectService,
    private fb: FormBuilder,
    private lightshowService: LightShowService,
    private authService: AuthService,
    private changeDetector: ChangeDetectorRef,
    private venueService: VenueService,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef
  ) { }
  @Input()
  public form: FormGroup;
  @Input()
  public selectTrack = false;
  public mp3Url: string;
  @Input()
  lightShowId: number;

  @Input()
  lightShowOwnerEngine: Engine;

  @Output()
  saveLightShow = new EventEmitter();

  @ViewChild('player', { static: false })
  private player: Mp3PlayerComponent;
  fadeOut: ElementRef;
  @ViewChild('start', { static: false })
  start: ElementRef;
  @ViewChild('duration', { static: false })
  duration: ElementRef;
  @ViewChild('fadeIn', { static: false })
  fadeInElement: ElementRef;
  @ViewChild('fadeOut', { static: false })
  fadeOutElement: ElementRef;
  @ViewChild('colorString', { static: false })
  colorString: ElementRef;
  @ViewChild('enterColorString', { static: false })
  enterColorString: ElementRef;
  @ViewChild('editColorString', { static: false })
  editColorString: ElementRef;
  @ViewChild('timeline', { static: false })
  timeline: ElementRef;
  @ViewChildren('segments')
  segmentElements: QueryList<any>;
  @ViewChild('imagesContainer', { static: false })
  imagesContainer: ElementRef;
  @ViewChildren('imageElements')
  imageElements: QueryList<any>;
  @ViewChild('colorsContainer', { static: false })
  colorsContainer: ElementRef;
  @ViewChildren('colorElements')
  colorElements: QueryList<any>;
  @ViewChild('enterEditColorString', { static: false })
  enterEditColorString: ElementRef;
  @ViewChild('backgroundColorString', { static: false })
  backgroundColorString: ElementRef;
  @ViewChild('enterBackgroundColorString', { static: false })
  enterBackgroundColorString: ElementRef;
  @ViewChild('timelineError', { static: false })
  timelineError: ErrorTooltipComponent;
  @ViewChild('libraryError', { static: false })
  libraryError: ErrorTooltipComponent;
  @ViewChild('dropZone', { static: false })
  dropZoneRef: ElementRef;
  @ViewChild('preview', { static: false })
  previewRef: ElementRef;

  @ViewChild(LightshowTorchEditorComponent) torchEditor: LightshowTorchEditorComponent;

  private _ngDestroy$ = new Subject<void>();


  invalidColorString: boolean;
  invalidEditColorString: boolean;
  invalidBackgroundColorString: boolean;

  // current user's engineId
  public currentUserEngine: Engine = null;

  // the torch+strobe operations defined in track.json
  public originalTrackOperations: any[] = [];
  // the adjusted strobe+torch operations (if cropEnd > 0, last operation can be extended and cropped operations are not present)
  public trackOperations: LightShowOperation[] = [];

  private _lightShowTypeCode: LightShowTypeCodes;
  private _track: LightShowTrack;

  public lightShowSize = 0;
  public lightShowSizeColor = 'rgba(0, 255, 0, 255)';
  public uploadingSoundEffect = false;
  public uploadingImageFile = false;
  public addingColor = false;
  public showSmartColorEditForm = false;
  public selectedColorMode: string = null;
  public soundEffects: SoundEffect[];
  public state = new LightshowState();
  public tabToShow = 'images';
  public imageIndexToHide: number;
  public colorIndexToHide: number;
  public soundEffectIndexToHide: number;
  public currentTime = 0;
  public colorToDisplay = 'black';
  public colorPending: boolean;
  public colorCounter = -1;
  public currentOperation = null;
  public previousOperation = null;
  public colorSegmentOpacity = 1;
  public imageSegmentOpacity = 1;
  public startFocus = false;
  public durationFocus = false;
  public fadeInFocus = false;
  public fadeOutFocus = false;
  private firstLoad = true;
  private deselectMode = false;

  public previousStrobePosition = 0;
  public strobeCounter = 0;

  public shouldSyncWithSlider = true;
  public currentSoundEffect = new Audio();

  public selectedColorSegment: LightShowSegment;
  public selectedImageSegment: LightShowSegment;
  public selectedSoundEffectSegment: LightShowSegment;
  public selectedOperations: LightShowOperation[] = [];
  public editorMode = EditorMode.SCREEN; // 0 - screen, 1 - torch
  public segmentMode: SegmentMode = SegmentMode.IMAGE;
  public torchArray = [];
  public isOnTopOfDragZone: boolean;
  public shouldFreeze: boolean;
  public vibrateSoundEffect: SoundEffect = {
    soundEffectId: -1,
    title: 'Vibrate',
    url: VIBRATE_SEGMENT_URL
  };

  private dropZoneEventListerners = ['dragenter', 'dragover', 'dragleave', 'drop'];

  private highlight: () => void;
  private unHighlight: () => void;
  private handleDrop: (e) => void;
  private DEFAULT_SEGMENT_LENGTH = 1000;
  public loading: boolean;

  @ViewChild('venueRef', { static: false })
  private venueRef: ElementRef;

  private _onDestroy$: Subject<void> = new Subject();

  public hoverTipShowing = false;
  public disableHoverTips = false;

  @ViewChild('disableToolTip', { static: false })
  disableToolTip: any;

  static initSegment(segment: LightShowSegment): LightShowSegment {
    if (segment.segmentType === LightshowSegmentTypes.addAudioLayer) {
      Object.setPrototypeOf(segment, AudioSegment.prototype);
    } else if (segment.segmentType === LightshowSegmentTypes.setImageAndBackgroundColor) {
      Object.setPrototypeOf(segment, ImageSegment.prototype);
    } else if (segment.segmentType === LightshowSegmentTypes.toggleColors && segment.colors.length > 1) {
      Object.setPrototypeOf(segment, ToggleSmartColorsSegment.prototype);
    } else if (segment.segmentType === LightshowSegmentTypes.toggleColors) { // FIND
      Object.setPrototypeOf(segment, ToggleSolidColorSegment.prototype);
    } else if (segment.segmentType === LightshowSegmentTypes.vibrate) { // FIND
      Object.setPrototypeOf(segment, VibrateSegment.prototype);
    }else if (segment.segmentType === LightshowSegmentTypes.screenStrobe){
      Object.setPrototypeOf(segment, ScreenStrobeSegment.prototype)
    } else if (segment.segmentType === LightshowSegmentTypes.Unknown) {
      Object.setPrototypeOf(segment, BlankSegment.prototype)
    } else {
      throw new Error('Invalid segment type');
    }
    segment.init();
    return segment;
  }

  static segmentToGroup(fb: FormBuilder, segment: LightShowSegment): FormGroup {
    const newSegmentFG = fb.group({
      uniqueid: Math.random()
        .toString(36)
        .slice(-5),
      position: [segment.position, Validators.min(0)],
      duration: [segment.duration, Validators.min(segment.minDuration)],
      frozen: [segment.frozen || false],
      fadeIn: [segment.fadeIn || 0],
      fadeOut: [segment.fadeOut || 0],
      minDuration: segment.minDuration,
      segmentType: segment.segmentType,
      color: segment.color,
      colors: fb.array(segment.colors || []),
      imageBackgroundColor: segment.imageBackgroundColor || '#000000',
      imageUrl: segment.imageUrl || '',
      imageName: segment.imageName || '',
      imageId: segment.imageId || Math.random().toString(36).slice(-5),
      audioUrl: segment.audioUrl || '',
      audioName: segment.audioName || '',
      hidden: segment.hidden || false
    });

    // when the segment type changes, update all controls that have validators
    newSegmentFG.controls.segmentType.valueChanges.subscribe(() => {
      FormHelpers.processControlRecursively(newSegmentFG, c => {
        if (c !== newSegmentFG.controls.segmentType) {
          c.updateValueAndValidity();
        }
      });
    });

    return newSegmentFG;
  }

  static colorToControl(color: string) {
    return new FormControl(
      color,
      LightshowPreviewComponent.validateCustomSegment(LightshowSegmentTypes.toggleColors, [Validators.required, CustomValidators.validateColor])
    );
  }

  static validateCustomSegment(segmentType: number, validators: ValidatorFn[]) {
    return (c: AbstractControl): ValidationErrors => {
      let seg: FormGroup;
      let assetsGroup: FormGroup;
      let temp = c.parent;
      while (temp && !(seg && assetsGroup)) {
        if (temp.controls['uniqueid']) {
          seg = temp as FormGroup;
        } else if (temp.controls['customModeData']) {
          assetsGroup = temp as FormGroup;
        }
        temp = temp.parent;
      }

      if (seg && assetsGroup) {
        const lsType = assetsGroup.parent.get('lightShowTypeCode').value;
        const canHaveSegments = lsType === LightShowTypeCodes.libraryTrack || lsType === LightShowTypeCodes.videoWithLibraryTrack;
        if (canHaveSegments && assetsGroup.get('mode').value === LightShowDefinitionMode.Custom && seg.controls.segmentType.value === segmentType) {
          for (let i = 0; i < validators.length; i++) {
            const res = validators[i](c);
            if (res) {
              return res;
            }
          }
        }
      }
      return null;
    };
  }

  onHoverTipShow() {
    this.disableToolTip.show();
    this.hoverTipShowing = true;
  }

  onHoverTipHide() {
    this.disableToolTip.hide();
    this.hoverTipShowing = false;
  }

  toggleHoverTips() {
    this.disableHoverTips = !this.disableHoverTips;

    localStorage.setItem('disableHoverTips', this.disableHoverTips ? 't' : 'f');
  }

  public venueChange() {
    const t = this.venueId;
    this.venue = this.userVenues.find(v => v.venueId === this.venueId);
      if (this.venue) {
      this.venue.venueGroups = [];
      if (this.venue.venueSections) {
        this.venue.venueSections.sort((a, b) => a.name.localeCompare(b.name));
        this.venue.venueSections.map(s => {
          s.selected = false;
          this.venue.venueGroups.push(...s.groups);
        });
        this.venue.venueGroups = Array.from(new Set(this.venue.venueGroups)).filter(g => g !== '')
          .sort((a, b) => a.localeCompare(b));
      }
      this.selectedVenue.emit(this.venue);
    } else {
      this.selectedVenueIdValue = 0;
    }
  }

  ngOnInit() {
    this.loading = true;

    this.disableHoverTips = localStorage.getItem('disableHoverTips') === 't';

    this.loadTrackData();

    this.authservice.getCurrentUser()
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(user => {
        if (user) {
          this.currentUserEngine = user.engine;
        }
      });

    this.venueService.get().subscribe(venues => {
      this.userVenues = venues;
      this.venueChange();
    });

    this.soundEffectService.getSoundEffects()
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(data => {
        this.soundEffects = data;
      });

    this.lightshowService.torchFreezer$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(shouldFreeze => this.shouldFreeze = shouldFreeze);

    this.lightshowService.operationArrayChange$
      .pipe(
        debounceTime(500),
        takeUntil(this._onDestroy$)
      ).subscribe(_ => {
        this.updateLightshowJson();
        this.trackOperations = this.originalTrackOperations;
        this.loading = false;
      });

      // HERE
      this.lightshowService.selectedOperationChange$
      .pipe(
        takeUntil(this._onDestroy$)
      ).subscribe(_ => {
        this.deselectMode = false;
        if (!this.venue) {
          return;
        }
        this.venue.venueSections.map(s => s.selected = false);
        this.selectedGroup = '';
        this.lightshowService.selectedOperations.forEach(o => {
          const selectedOperation = this.trackOperations.find(ou => ou.uniqueId === o);

          if (selectedOperation && selectedOperation.sectionList) {
            const opertionGroups = selectedOperation.sectionList.filter(s => this.venue.venueGroups.find(g => g === s));
            if (opertionGroups.length > 0) {
              this.selectedGroup = opertionGroups[0];
            }

          if (selectedOperation.sectionList.indexOf(this.selectedGroup) >= 0) {
            this.venue.venueSections.map(v => v.selected = v.groups.indexOf(this.selectedGroup) >= 0);
          } else {
            this.venue.venueSections.map(s =>
              s.selected = !selectedOperation.sectionList || selectedOperation.sectionList.length === 0 || selectedOperation.sectionList.indexOf(s.name) >= 0);
            }
          }
        });
        this.updateCanvas();
      });


    // This tracks changes to track selection from user and cleans the segments
    this.lightshowService.trackIdManualChange$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        this.cleanSegments();
      });

    this.lightshowService.openFullScreen$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(() => {
        this.lightshowService.showFullScreenPreview = true;

        this.changeDetector.detectChanges();

        if (this.preview) {
          if ((this.preview as HTMLDivElement).requestFullscreen) {
            this.preview.requestFullscreen();
          } else if (this.preview.mozRequestFullScreen) { /* Firefox */
            this.preview.mozRequestFullScreen();
          } else if (this.preview.webkitRequestFullscreen) { /* Chrome, Safari and Opera */
            this.preview.webkitRequestFullscreen(this.preview.ALLOW_KEYBOARD_INPUT);
            document.addEventListener('webkitfullscreenchange', () => {
              if (document['webkitIsFullScreen']) {
                this.lightshowService.showFullScreenPreview = true;
              }
            }, false);
            return;
          } else if (this.preview.msRequestFullscreen) { /* IE/Edge */
            this.preview.msRequestFullscreen();
          }
        }
      });

      this.lightshowService.deleteKeyPressed$
        .pipe(takeUntil(this._ngDestroy$))
        .subscribe(
          _ => {

            const focusedInput = this.startFocus || this.durationFocus || this.fadeInFocus || this.fadeOutFocus ||
                         this.player?.zoomFocus || this.torchEditor?.startFocus || this.torchEditor?.durationFocus

            if (focusedInput) {

              return;
            }

            if (this.editorMode === EditorMode.SCREEN && this.selectedSegmentValue) {

              const targetSegment = this.segments.find(seg => seg.uniqueid === this.selectedSegmentValue.uniqueid);
              const targetIndex = this.segments.indexOf(targetSegment);

              const segmentsFA = this.form.get('customModeData.segments') as FormArray;
              segmentsFA.removeAt(targetIndex);

            } else {

              for (const uniqueId of this.lightshowService.selectedOperations) {

                  const targetOperation = this.trackOperations.find(op => op.uniqueId === uniqueId);
                  const targetIndex = this.trackOperations.indexOf(targetOperation);
                  this.torchEditor.deleteOperation(targetIndex);
              }
            }
          }
        )

    this.updateLightshowJson();
  }

  ngAfterViewInit() {
    this.player.playbackStarted.pipe(takeUntil(this._onDestroy$)).subscribe(_ => this.state.isPlaying = true);
    this.player.playbackPaused.pipe(takeUntil(this._onDestroy$)).subscribe(_ => { this.state.isPlaying = false; this.currentSoundEffect.pause(); });
    this.player.playbackStopped.pipe(takeUntil(this._onDestroy$)).subscribe(_ => { this.state.isPlaying = false; this.currentSoundEffect.pause(); });
    this.player.playPositionChanged
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(pos => {
        this.shouldSyncWithSlider = true;
        this.seekPosition(pos);
        this.animateLightshow(pos);
      });
    this.player.durationChanged.pipe(takeUntil(this._onDestroy$)).subscribe(_ => this.loadTrackData());
    this.player.cropEndPercentChanged
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(cp => {
        this.form.get('cropEndPercent').setValue(cp);
        this.trackOperations = this.originalTrackOperations;
        this.loading = false;
      });


    // recalculate standard segments and track operations when end cropped
    this.form.get('cropEndPercent').valueChanges
      .pipe(
        debounceTime(500),
        takeUntil(this._onDestroy$)
      )
      .subscribe(_ => {
        if (this.track) {
          this.seekPosition(this.state.playbackPosition);
          this.trackOperations = this.originalTrackOperations;
          this.loading = false;
        }
      });

    const lsChangeHandler = () => {
      this.updateLightshowJson();
      if (!this.state.isPlaying) {
        this.seekPosition(this.state.playbackPosition);
      }
    };

    // update lightshow when data changes
    this.form.get('customModeData.segments').valueChanges
      .pipe(
        debounceTime(1000), // No need to fire this whenever segment is moved even one pixel
        takeUntil(this._onDestroy$)
      )
      .subscribe(lsChangeHandler);

    this.lightshowService.sortSegments
      .pipe(
        debounceTime(500),
        takeUntil(this._onDestroy$)
      )
      .subscribe(_ => {
        this.sortSegments();
      });

    this.addDropZoneEventListeners();
  }

  preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['track']) {
      if (this.track && this.track.trackId > 0) {
        this.trackService.getTrack(this.track.trackId).subscribe(track => {
          this.track = track;

          this.loadTrackData();
          this.updateLightshowJson();
        });
      } else {
        this.loadTrackData();
        this.updateLightshowJson();
      }
    }
  }

  addDropZoneEventListeners() {
    // Add Drag and Drop event listeners
    this.dropZoneEventListerners.forEach(eventName => {
      this.dropZone.addEventListener(eventName, this.preventDefaults, false);
    });

    this.highlight = () => {
      this.isOnTopOfDragZone = true;
      this.dropZone.classList.add('upload-highlight');
    };

    this.unHighlight = () => {
      this.isOnTopOfDragZone = false;
      this.dropZone.classList.remove('upload-highlight');
    };

    this.handleDrop = (e) => {
      const dt = e.dataTransfer;
      const files = dt.files;

      this.addImageToLibrary(files);
    };

    ['dragenter', 'dragover'].forEach(eventName => {
      this.dropZone.addEventListener(eventName, this.highlight, false);
    });

    ['dragleave', 'drop'].forEach(eventName => {
      this.dropZone.addEventListener(eventName, this.unHighlight, false);
    });

    this.dropZone.addEventListener('drop', this.handleDrop, false);
  }

  scrollTimeline = () => {
    if (this.timeline) {
      this.timeline.nativeElement.scrollLeft = this.timeline.nativeElement.scrollWidth;
    }
  }

  scrollImages = () => {
    if (this.imagesContainer) {
      this.imagesContainer.nativeElement.scrollTop = this.imagesContainer.nativeElement.scrollHeight;
    }
  }

  scrollColors = () => {
    if (this.colorsContainer) {
      this.colorsContainer.nativeElement.scrollTop = this.colorsContainer.nativeElement.scrollHeight;
    }
  }

  changeEditorMode(mode: string) {
    if (mode === 'SCREEN') {
      this.editorMode = EditorMode.SCREEN;
    }
    if (mode === 'TORCH') {
      this.editorMode = EditorMode.TORCH;
    }
  }

  changeSegmentMode (mode: string) {

    if (mode === 'COLOR') {

      this.tabToShow = 'colors';
      this.invalidColorString = false;
      this.invalidEditColorString = false;
      this.segmentMode = SegmentMode.COLOR;

    } else if (mode === 'IMAGE') {

      this.tabToShow = 'images';
      this.invalidBackgroundColorString = false;
      this.segmentMode = SegmentMode.IMAGE;

    } else if (mode === 'SOUND') {

      this.tabToShow = 'audio';
      this.segmentMode = SegmentMode.SOUND;
    }
  }

  compareSegments(firstSegment: LightShowSegment, secondSegment: LightShowSegment) {
    if (firstSegment && secondSegment) {
      return this.segments.indexOf(firstSegment) === this.segments.indexOf(secondSegment);
    } else {
      return false;
    }
  }

  updateLightshowJson() {
    const operations = this.combineOperations();

    const colors = operations.filter(op => +op.flashType === FlashTypes.color);

    colors.forEach((c1, index) => {
      if (index !== colors.length - 1) {
        const c2 = colors[index + 1];
        if (c1.position >= (c2.position - 100) && c1.position <= (c2.position + 100)) {
          if (c1.color === '#000000') {
            operations.splice(operations.indexOf(c1), 1);
          } else if (c2.color === '#000000') {
            operations.splice(operations.indexOf(c2), 1);
          }
        }
      }
    });

    const formattedOperations = operations
      .map(op => op.toJson(operations))
      .reduce((a, b) => a.concat(b), [])
      .sort((s1, s2) => s1.time - s2.time);

    const allAudioUrls = operations.filter(op => op instanceof PlayAudioOperation || op instanceof VibrateOperation).map(op => op.audioUrl);

    allAudioUrls.forEach(url => {
      let flag = false;
      formattedOperations.forEach((op, index) => {
        if (op.url === url && op.type === FlashTypes.audioPrefetch) {
          if (flag) {
            formattedOperations.splice(index, 1);
          } else {
            flag = true;
          }
        }
      });
    });

    const allImageUrls = operations.filter(op => op instanceof ImageFadeinnOperation).map(op => op.imageUrl);

    allImageUrls.forEach(url => {
      let flag = false;
      formattedOperations.forEach((op, index) => {
        if (op.url === url && op.type === FlashTypes.imagePrefetch) {
          if (flag) {
            formattedOperations.splice(index, 1);
          } else {
            flag = true;
          }
        }
      });
    });

    const newJson = JSON.stringify(formattedOperations);
    const jsonControl = this.form.get('lightShowJson');
    if (jsonControl.value !== newJson) {
      jsonControl.setValue(newJson);
    }

    this.loading = false;
  }

  animateLightshow(position: number) {
    // get an array from all segments that contain the music player slider
    const selectedSegments = this.segments.filter(seg => {
      const startPosition = seg.position;
      const endPosition = seg.position + seg.duration;
      if (position >= startPosition && position <= endPosition) {
        return true;
      }
    });

    // assign the segments to their appropriate selector
    selectedSegments.forEach(seg => {
      if (this.getSegmentType(seg) === SegmentType.SoundEffect) {
        this.selectedSoundEffectSegment = seg;
      } else if (this.getSegmentType(seg) === SegmentType.Image) {
        this.selectedImageSegment = seg;
      } else if (this.getSegmentType(seg) === SegmentType.SmartColor) {
        this.selectedColorSegment = seg;
      } else if (this.getSegmentType(seg) === SegmentType.SolidColor) {
        this.selectedColorSegment = seg;
      } else if (this.getSegmentType(seg) === SegmentType.ScreenStrobe){
        this.selectedColorSegment = seg;
      }
    });
    this.updateFadeInAndFadeOut();

    // clean up any wrongly selected ones. This should be refactored
    if (!selectedSegments.includes(this.selectedSoundEffectSegment)) {
      this.selectedSoundEffectSegment = null;
      if (!this.currentSoundEffect.paused && this.currentSoundEffect.readyState > 2 && !this.currentSoundEffect.ended) {
        this.currentSoundEffect.pause();
      }
    }

    if (!selectedSegments.includes(this.selectedImageSegment)) {
      this.selectedImageSegment = null;
    }

    if (!selectedSegments.includes(this.selectedColorSegment)) {
      this.selectedColorSegment = null;
    }

    // set color to display to segments duration / number of colors

    if (this.selectedColorSegment) {

      if (this.getSegmentType(this.selectedColorSegment) === SegmentType.SmartColor) {
        let changeDetector = false;
        const pos = this.player.playPosition;
        const torchOn = this.originalTrackOperations.find(op => op.flashType === FlashTypes.torchOn && op.position <= pos && op.position + op.duration >= pos);
        const strobeOn = this.originalTrackOperations.find(op => op.flashType === FlashTypes.strobeOn && op.position <= pos && op.position + op.duration >= pos);

        // If we have a current operation and the position lies within a torch or strobe operation that is not the same as our current operation...
        if (this.currentOperation && ((torchOn && this.currentOperation?.uniqueId !== torchOn?.uniqueId) || (strobeOn && this.currentOperation?.uniqueId !== strobeOn?.uniqueId))){
          // Set the previous operation to what was our current operation
          this.previousOperation = this.currentOperation;
        } else {
          // Otherwise make sure our previous operation is null, since it's possible the position does not lie within a valid operation
          this.previousOperation = null;
        }
        this.currentOperation = torchOn ? torchOn : strobeOn ? strobeOn : null;

        // If we have a current and previous operation and they're overlapping, we don't try to change colors. changeDetector will end up false
        if (this.currentOperation && this.previousOperation && this.previousOperation.position + this.previousOperation.duration > this.currentOperation.position){
          this.currentOperation = null;
          this.previousOperation = null;
        }

        if (torchOn || strobeOn) {
          // Added logic: no color pending OR we have both a current and previous operation
          if ((!this.colorPending) || (this.previousOperation && this.currentOperation)) {
            changeDetector = true;
          }
          this.colorPending = true;
        } else {
          this.colorPending = false;
        }

        if (changeDetector) {
          // I've initialized colorCounter to start at -1, since we always increment it before using it to access colors
          // We might want to make it clear to the user in some way that colorToDisplay is initalized to black, therefore making the first color of a smartcolor black
          // will give the appearance that smart color transitions aren't working for the first transition.
          this.colorCounter += 1;
          if (this.colorCounter >= this.selectedColorSegment.colors.length) {
            this.colorCounter = 0;
          }

          if (this.colorToDisplay === 'black') {
            this.colorToDisplay = this.selectedColorSegment.colors[0];
          } else {
            this.colorToDisplay = this.selectedColorSegment.colors[this.colorCounter];
          }
        }
      } else if (this.getSegmentType(this.selectedColorSegment) === SegmentType.ScreenStrobe){
          const timeElapsed = this.state.playbackPosition - this.previousStrobePosition;

          if (timeElapsed < 0){
            this.previousStrobePosition = 0;
          }

          if (timeElapsed >= 100 || this.previousStrobePosition === 0){
            this.colorToDisplay = this.selectedColorSegment.colors[this.strobeCounter % this.selectedColorSegment.colors.length];
            this.previousStrobePosition = this.state.playbackPosition;
            this.strobeCounter++;
          }
      } else if (this.selectedColorSegment.colors.length === 1) {
        this.colorToDisplay = this.selectedColorSegment.colors[0];
      }

    } else if (this.colorToDisplay !== 'black'){
      this.colorToDisplay = 'black';
    }

    // fade in and fade out logic for color segments
    if (this.selectedColorSegment) {
      const fadein = +this.selectedColorSegment.fadeIn;
      const fadeout = +this.selectedColorSegment.fadeOut;
      const pos = +this.selectedColorSegment.position;
      const dur = +this.selectedColorSegment.duration;

      if (position <= pos + fadein) {
        this.colorSegmentOpacity = (position - pos) / fadein;
      } else if (position >= pos + dur - fadeout) {
        this.colorSegmentOpacity = (position - pos - dur) / -fadeout;
      } else {
        this.colorSegmentOpacity = 1;
      }
    }

    // fade in and fade out logic for image segments
    if (this.selectedImageSegment) {
      const fadein = +this.selectedImageSegment.fadeIn + this.minFadeIn;
      const fadeout = +this.selectedImageSegment.fadeOut;
      const pos = +this.selectedImageSegment.position;
      const dur = +this.selectedImageSegment.duration;
      if (position <= pos + fadein) {
        this.imageSegmentOpacity = (position - pos) / fadein;
      } else if (position >= pos + dur - fadeout) {
        this.imageSegmentOpacity = (position - pos - dur) / - fadeout;
      } else {
        this.imageSegmentOpacity = 1;
      }

      if (!this.selectedColorSegment) {
        this.colorToDisplay = this.selectedImageSegment.imageBackgroundColor;
      }
    }

    // handle sound effects
    if (this.selectedSoundEffectSegment && this.state.isPlaying) {
      if (this.currentSoundEffect.src !== this.selectedSoundEffectSegment.audioUrl) {
        this.currentSoundEffect.src = this.selectedSoundEffectSegment.audioUrl;
      }
      const playPromise = this.currentSoundEffect.play();
      if (playPromise !== undefined) {
        playPromise.then(_ => {
          const pos = +this.selectedSoundEffectSegment.position;
          const time = (position - pos) / 1000;
          const MAXIMUM_DIFFERENCE_BEFORE_SYNCING = 0.2;
          if (this.shouldSyncWithSlider && Math.abs(time - this.currentSoundEffect.currentTime) > MAXIMUM_DIFFERENCE_BEFORE_SYNCING) {
            this.currentSoundEffect.currentTime = time;
            this.shouldSyncWithSlider = false;
          }
        })
          .catch(_ => _);
      }
    }

    this.updateCanvas();
  }

  getSegmentType(seg: LightShowSegment): SegmentType {
    if (seg && seg.audioUrl && seg.audioName) {
      return SegmentType.SoundEffect;
    } else if (seg && seg.colors.length > 1 && seg.segmentType === 5) {
      return SegmentType.SmartColor;
    } else if (seg && seg.colors.length === 1 && seg.segmentType === 5) {
      return SegmentType.SolidColor;
    } else if (seg && seg.segmentType === 6) {
      return SegmentType.ScreenStrobe;
    }else if (seg && seg.imageUrl && (seg.segmentType === 16 || seg.segmentType === 31)) {
      return SegmentType.Image;
    }

    return SegmentType.Unknown;
  }

  seekPosition(position: number) {
    // this.currentTime = position;
    this.state.seekPosition(position, this.combineOperations());
    // I *think* this is a good idea. If the seek position is dragged or end up back at the beginning,
    // The colorToDisplay and color counter should be reset.
    if (position === 0){
      this.colorToDisplay = 'black';
      this.colorCounter = -1
    }
  }

  setSegmentColor(colors: string[], strobe: boolean = false) {
    // Check both solid and smart color types of overlap
    const incompatibleSolid = this.checkForOverlappingSegments(SegmentType.SolidColor);
    const incompatibleSmart = this.checkForOverlappingSegments(SegmentType.SmartColor);
    const incompatibleStrobe = this.checkForOverlappingSegments(SegmentType.ScreenStrobe);

    const incompatibleElement = !!incompatibleSolid || !!incompatibleSmart || !! incompatibleStrobe;

    if (!incompatibleElement) {
      if (this.selectedSegment) {
        const fa = this.selectedSegment.get('colors') as FormArray;

        while (fa.length !== 0) {
          fa.removeAt(0);
        }

        this.selectedSegment.patchValue({
          imageUrl: '',
          imageName: '',
          imageId: '',
          audioName: '',
          audioUrl: '',
          imageBackgroundColor: '',
          segmentType: strobe ? LightshowSegmentTypes.screenStrobe : LightshowSegmentTypes.toggleColors,
        });

        colors.forEach(c => {
          fa.push(LightshowPreviewComponent.colorToControl(c));
        });

        fa.markAsDirty();
      }
    } else if (this.selectedSegment) {
      this.timelineError.showAndHide('The selected segment will overlap with a Color Segment.');
    } else {
      this.timelineError.showAndHide('No Segment has been selected');
    }
    this.updateLightShowSize();
  }

  addColorToLibrary(colors: string[], enableStrobe: boolean = false, type: 'backgroundColorString' | 'editColorString' | 'colorString', colorValue: string) {

    if (!(colorValue === 'smart') && !this.onSaveColor(type, colors[0])) {
      this.invalidColorString = true;
      return;
    }

    const filteredColors = colors.filter(c => {
      return c.startsWith('#') && c.length === 7;
    });

    if (filteredColors.length === 1) {
      this.colorsLibrary.push({
        type: 'solid',
        color: filteredColors[0]
      });

      this.addingColor = false;
      this.selectedColorMode = null;
    } else if (filteredColors.length > 1) {
      if (enableStrobe){
        this.colorsLibrary.push({
          type: 'strobe',
          colors: filteredColors,
        });
      } else {
        this.colorsLibrary.push({
          type: 'smart',
          colors: filteredColors,
        });
      }

      this.addingColor = false;
      this.selectedColorMode = null;
    } else {
      this.libraryError.showAndHide('Please add at least one valid color.');
    }

    setTimeout(() => this.scrollColors(), 100);
  }

  checkForOverlappingSegments(type: SegmentType, segment: LightShowSegment = this.selectedSegmentValue): LightShowSegment | undefined {
    if (segment) {
      return this.segments.find(seg =>
        this.getSegmentType(seg) === type &&
        this.getSegmentType(segment) !== type &&
        segment.uniqueid !== seg.uniqueid &&
        ((segment.position > seg.position &&
          segment.position < seg.position + seg.duration) ||
          (segment.position + segment.duration > seg.position &&
            segment.position + segment.duration < seg.position + seg.duration))
      );
    } else {
      return undefined;
    }
  }

  setSegmentImage(image: LibraryImage) {

    const incompatibleElement = this.checkForOverlappingSegments(SegmentType.Image);

    const engine = this.lightShowOwnerEngine ?? this.currentUserEngine;

    if (this.selectedSegment && restrictImageSegmentStartTime(this.selectedSegmentValue, engine)) {
      this.timelineError.showAndHide(`Image Segments cannot be placed within the first ${engine.imageSegmentStartTimeThreshold} seconds of a light show`);
    } else if (this.selectedSegment && !incompatibleElement) {
      const seg = this.selectedSegment;
      const fa = this.selectedSegment.controls.colors as FormArray;
      seg.patchValue({
        imageUrl: image.url,
        imageName: image.name,
        imageId: image.location,
        imageBackgroundColor: image.imageBackgroundColor,
        audioName: '',
        audioUrl: '',
        segmentType: LightshowSegmentTypes.setImageAndBackgroundColor
      });

      while (fa && fa.length !== 0) {
        fa.removeAt(0);
      }

      seg.controls.imageName.markAsDirty();
      seg.controls.imageUrl.markAsDirty();
    } else if (this.selectedSegment) {
      this.timelineError.showAndHide('The selected segment will overlap with an Image Segment.');
    } else {
      this.timelineError.showAndHide('No Segment has been selected.');
    }
    this.updateLightShowSize();
  }

  addImageToLibrary(files: File[], libIndex: number = -1) {

    this.libraryError.hide();

    if (files.length > 0) {

      if (files[0].type !== "image/jpeg" && files[0].type !== "image/gif" && files[0].type !== "image/png") {
        this.libraryError.show('Unsupported file type. Please upload jpeg, jpg, png, or gif files.')
        return;
      }

      if (files[0].name.toLowerCase().endsWith('.gif')) {
        if (files[0].size > 5 * 1024 * 1024) { // return error if over 5mb
          this.libraryError.show('The GIF file you are attempting to upload is greater than 5MB in size, please consider resizing the GIF to continue');
          return;
        }
      }

      this.uploadingImageFile = true;
      let replacedImage = null;
      this.photoService
        .enqueueUploadPhoto(files[0], Math.random().toString(36).slice(-5), { height: 1920, width: 1080 }, false, false)
        .pipe(takeUntil(this._onDestroy$))
        .subscribe(res => {
          if (res.stage === 1) {
            const libraryImage = {
              imageBackgroundColor: '#000000',
              size: files[0].size,
              name: res.name,
              location: res.location,
              stage: res.stage,
              url: res.url
            };

            if (libIndex >= 0){

              replacedImage = this.imageLibrary.splice(libIndex, 1, libraryImage)[0];

              const serviceFile = this.serviceFiles.files.find(f => f.file.name === replacedImage.name);
              const serviceFileIndex = this.serviceFiles.files.indexOf(serviceFile);

              if (serviceFileIndex > -1) {

                this.serviceFiles.files[serviceFileIndex].file = files[0];

              } else {

                this.serviceFiles.addImage(files[0]);
              }

            } else {

              this.serviceFiles.addImage(files[0]);
              this.imageLibrary.push(libraryImage);
            }

            this.segments.filter(i => i.imageUrl === replacedImage?.url || i.imageName === libraryImage.name)
              .forEach(seg => {
                seg.imageName = res.name;
                seg.imageUrl = res.url;
                seg.imageId = res.location;
              });

            this.uploadingImageFile = false;
          } else if (res.stage === 2) {

            this.segments.filter(i => i.imageId === res.location)
              .forEach(seg => {
                seg.imageUrl = res.url;
              });

            this.imageLibrary.filter(i => i.location === res.location)
              .forEach(image => {
                image.url = res.url;
              });

            this.updateLightshowJson();
          }

          setTimeout(() => this.scrollImages(), 100);
        },
          (error) => {
            this.uploadingImageFile = false;
            this.libraryError.show(error.reason);
          },
          () => {
            this.uploadingImageFile = false;
          }
        );
    }
    this.imageIndexToHide = null;
    this.updateLightShowSize();
  }

  changeImageBackgroundColor(index: number, color: string) {
    if (color.length === 4 || color.length === 7 && color.startsWith('#')) {
      this.imageLibrary[index].imageBackgroundColor = color;
      this.imageIndexToHide = null;
    } else {
      this.timelineError.showAndHide('Invalid color');
    }
  }

  deleteImage(image: LibraryImage) {
    // const foundSegment = this.segments.find(s => s.imageUrl === image.url);

    // console.log(this.segments);

    // handle case where image is attached of a segment
    // if (this.selectedSegment && (this.selectedSegmentValue.imageName === this.imageLibrary[index].name)) {
    //   this.selectedSegment.patchValue({ imageUrl: '', imageName: '', imageBackgroundColor: '#000000', segmentType: LightshowSegmentTypes.toggleColors });
    //   const fa = this.selectedSegment.get('colors') as FormArray;

    //   // after deleting the image set the segment to the default - black color segment
    //   fa.push(LightshowPreviewComponent.colorToControl('#000000'));
    // }

    const index = this.imageLibrary.indexOf(image);

    this.imageLibrary.splice(index, 1);
    this.imageIndexToHide = null;

    const serviceFile = this.serviceFiles.files.find(f => f.file.name === image.name);
    const serviceFileIndex = this.serviceFiles.files.indexOf(serviceFile);

    if (serviceFileIndex > -1) {

      this.serviceFiles.files.splice(serviceFileIndex, 1);
    }
  }

  editSolidColor(index: number, color: string) {

    if (color.length === 4 || color.length === 7 && color.startsWith('#')) {
      this.colorsLibrary[index].color = color;
      this.colorIndexToHide = null;
    } else {
      this.libraryError.showAndHide('Please enter a valid color.');
    }
  }

  editSmartColor(index: number, colors: string[]) {

    const filteredColors = colors.filter(c => {
      return c.startsWith('#') && c.length === 4 || c.length === 7;
    });

    if (filteredColors.length === 1) {
      this.colorsLibrary[index] = {
        type: 'solid',
        color: filteredColors[0]
      };

      this.colorIndexToHide = null;
      this.showSmartColorEditForm = false;
    } else if (filteredColors.length > 1) {
      this.colorsLibrary[index] = {
        type: 'smart',
        colors: filteredColors,
      };

      this.showSmartColorEditForm = false;
      this.colorIndexToHide = null;
    } else {
      this.libraryError.showAndHide('Please enter at least one valid color.');
    }
  }

  editStrobeColor(index: number, colors: string[]){

    const filteredColors = colors.filter(c => {
      return c.startsWith('#') && c.length === 4 || c.length === 7;
    });

    if (filteredColors.length === 1) {
      this.colorsLibrary[index] = {
        type: 'solid',
        color: filteredColors[0]
      };

      this.colorIndexToHide = null;
      this.showSmartColorEditForm = false;
    } else if (filteredColors.length > 1) {
      this.colorsLibrary[index] = {
        type: 'strobe',
        colors: filteredColors,
      };

      this.showSmartColorEditForm = false;
      this.colorIndexToHide = null;
    } else {
      this.libraryError.showAndHide('Please enter at least one valid color.');
    }
  }

  deleteColor(index: number) {
    if (this.selectedSegment && (this.selectedSegmentValue.colors[0] === this.colorsLibrary[index].color)) {
      this.selectedSegment.patchValue({ colors: ['#000000'] });
    }

    this.colorsLibrary.splice(index, 1);
    this.colorIndexToHide = null;
  }

  uploadSoundEffect(files: File[]) {

    this.libraryError.hide();

    if (files.length > 0) {

      if (files[0].type !== 'audio/mpeg'){

        this.libraryError.show("Unsupported file type. Please upload an .mp3 file.");
        return;
      }
      this.uploadingSoundEffect = true;
      this.soundEffectService
        .uploadSoundEffect(files[0])
        .pipe(takeUntil(this._onDestroy$))
        .subscribe(
          res => {
            this.soundEffects.push(res);
          },
          () => null,
          () => (this.uploadingSoundEffect = false)
        );
    }
  }

  deleteSoundEffect (index: number) {

    const effect = this.soundEffects.splice(index, 1);

    if (effect && effect.length > 0) {

      this.soundEffectService.deleteSoundEffect(effect[0].soundEffectId)
        .pipe(take(1)).subscribe(
          _ => {

           var segmentsFormArray = this.form.get('customModeData.segments') as FormArray;

            for (var i = segmentsFormArray.length - 1; i >= 0; --i) {

              if (segmentsFormArray.value[i].audioUrl === effect[0].url) {

                segmentsFormArray.removeAt(i);
              }
            }

            this.dialogService.alert("Success", "That sound effect has been successfully deleted.", this.viewRef);
          },
          error => {

            this.dialogService.alert("Uh oh!", `We were not able to delete that sound effect: ${error.error}`, this.viewRef);
          }
        )
    }
  }

  setSegmentSoundEffect(selectedSoundEffect: SoundEffect) {
    const IS_VIBRATE_SEGMENT = selectedSoundEffect.soundEffectId === -1;
    const incompatibleElement = IS_VIBRATE_SEGMENT
      ? this.checkForOverlappingSegments(SegmentType.Vibrate)
      : this.checkForOverlappingSegments(SegmentType.SoundEffect);

    const trackDurationMs = moment.duration(this.track.duration).asMilliseconds();
    const cutOff = trackDurationMs - (this.cropEndPercent / 100) * trackDurationMs;

    if (this.selectedSegment && !incompatibleElement) {
      this.currentSoundEffect.src = selectedSoundEffect.url;

      this.currentSoundEffect.onloadedmetadata = () => {
        // Check to see if setting sound effect on selected segment won't go inside cutoff
        if (this.selectedSegmentValue.position + (this.currentSoundEffect.duration * 1000) < cutOff) {

          this.selectedSegment.patchValue({
            audioName: selectedSoundEffect.title,
            audioUrl: selectedSoundEffect.url,
            imageName: '',
            imageUrl: '',
            imageId: '',
            imageBackgroundColor: '',
            segmentType: IS_VIBRATE_SEGMENT ? LightshowSegmentTypes.vibrate : LightshowSegmentTypes.addAudioLayer
          });

          this.currentSoundEffect.ontimeupdate = () => {
            this.shouldSyncWithSlider = true;
          };

          // selectedSegment is soundEffect so remove all colors
          const fa = this.selectedSegment.controls.colors as FormArray;

          while (fa.length !== 0) {
            fa.removeAt(0);
          }

          if (this.selectedSegment.value.audioUrl === this.currentSoundEffect.currentSrc) {
            this.selectedSegment.patchValue({ duration: IS_VIBRATE_SEGMENT ? 300 : this.currentSoundEffect.duration * 1000 });
          }
        } else {
          this.currentSoundEffect = new Audio();
          this.timelineError.showAndHide('Sound Effect segment will be outside of track boundaries.');
        }
      };
    } else if (this.selectedSegment) {
      this.timelineError.showAndHide('The selected segment will overlap with a Sound Effect Segment.');
    } else {
      this.timelineError.showAndHide('No Segment has been selected');
    }
    this.updateLightShowSize();
  }

  setSelectedSegmentPosition(position: number) {
    const originalSelectedSegmentStart = this.selectedSegmentValue.position;

    const wantedSelectedSegmentEnd = position + this.selectedSegmentValue.duration;

    const MINIMUM_SEGMENT_POSITION = 50;
    if (position >= MINIMUM_SEGMENT_POSITION) {
      for (const s of this.segments) {
        const engine = this.lightShowOwnerEngine ?? this.currentUserEngine;

        if (this.getSegmentType(s) === SegmentType.Image && restrictImageSegmentStartTime(s, engine)) {
          this.selectedSegment.patchValue({ position: originalSelectedSegmentStart });
          this.start.nativeElement.value = (originalSelectedSegmentStart / 1000).toFixed(2);
          this.timelineError.showAndHide(`Image Segments cannot be placed within the first ${engine.imageSegmentStartTimeThreshold} seconds of a light show`);
        } else if (this.selectedSegmentValue.uniqueid !== s.uniqueid && this.getSegmentType(this.selectedSegmentValue) === this.getSegmentType(s) && position > 0) {
          const currentSegmentStart = s.position;
          const currentSegmentEnd = s.position + s.duration;

          if ((position > currentSegmentStart && position < currentSegmentEnd) ||
            (wantedSelectedSegmentEnd > currentSegmentStart && wantedSelectedSegmentEnd <= currentSegmentEnd) ||
            (position < currentSegmentStart && wantedSelectedSegmentEnd > currentSegmentEnd)) {
            this.selectedSegment.patchValue({ position: originalSelectedSegmentStart });
            this.start.nativeElement.value = (originalSelectedSegmentStart / 1000).toFixed(2);

            this.timelineError.showAndHide('Segment is overlapping with a segment of the same type');
            break;
          }
        } else {
          this.selectedSegment.patchValue({ position: Math.min(Math.max(0, position), this.player.totalDuration - this.selectedSegment.controls.duration.value) });
        }
      }
    } else {
      this.selectedSegment.patchValue({ position: originalSelectedSegmentStart });
      this.start.nativeElement.value = (originalSelectedSegmentStart / 1000).toFixed(2);

      this.timelineError.showAndHide(`Segment start cannot be a negative number or smaller than ${MINIMUM_SEGMENT_POSITION / 1000} seconds`);
    }

    this.sortSegments();

    this.selectedSegment.controls.position.updateValueAndValidity();
    this.selectedSegment.controls.position.markAsDirty();
  }

  setSelectedSegmentDuration(duration: number) {
    const initialDuration = this.selectedSegmentValue.duration;
    const stop = Math.abs(this.selectedSegmentValue.position + duration);
    const wantedSelectedSegmentEnd = this.selectedSegmentValue.position + duration;
    const minDuration = this.selectedSegmentValue.minDuration;
    const trackDurationMs = moment.duration(this.track.duration).asMilliseconds();
    const cutOff = trackDurationMs - (this.cropEndPercent / 100) * trackDurationMs;

    if (stop < 0 || duration < minDuration) {
      this.selectedSegment.patchValue({ duration: minDuration });
      this.duration.nativeElement.value = (minDuration / 1000).toFixed(2);

      this.timelineError.showAndHide('Segment duration is below minimum');
    } else
      if (stop > cutOff) {
        this.selectedSegment.patchValue({ duration: initialDuration });
        this.duration.nativeElement.value = (initialDuration / 1000).toFixed(2);

        this.timelineError.showAndHide('Segment can\'t end after the track');
      } else {
        if (duration !== this.selectedSegmentValue.duration) {
          const incompatibleElement = this.segments.find(seg => ((wantedSelectedSegmentEnd > seg.position
            && wantedSelectedSegmentEnd < seg.position + seg.duration)
            || (this.selectedSegmentValue.position < seg.position
              && wantedSelectedSegmentEnd > seg.position + seg.duration))
            && this.getSegmentType(seg) === this.getSegmentType(this.selectedSegmentValue)
            && seg.uniqueid !== this.selectedSegmentValue.uniqueid);

          if (!incompatibleElement && this.getSegmentType(this.selectedSegmentValue) !== SegmentType.SoundEffect) {
            this.selectedSegment.patchValue({ duration });
          } else {
            this.duration.nativeElement.value = (this.selectedSegmentValue.duration / 1000).toFixed(2);

            this.timelineError.showAndHide('Segment will overlap with a segment of the same type');
          }
        }
      }

    this.updateFadeInAndFadeOut();
    this.sortSegments();

    this.selectedSegment.controls.duration.updateValueAndValidity();
    this.selectedSegment.controls.duration.markAsDirty();
  }

  setSelectedSegmentFadeInOrFadeOut(value: number, type: string) {
    const duration = this.selectedSegmentValue.duration;

    if (type === 'fadeIn') {
      if (this.getSegmentType(this.selectedSegmentValue) !== SegmentType.SoundEffect && value > 0) {
        if (value >= duration - this.selectedSegmentValue.fadeOut) {
          this.selectedSegment.patchValue({ fadeIn: (duration - this.selectedSegmentValue.fadeOut).toFixed(2) });

          this.timelineError.showAndHide('Fade-in value is more than the segment\'s duration');
        } else {
          this.selectedSegment.patchValue({ fadeIn: value.toFixed(2) });
        }
      } else {
        this.selectedSegment.patchValue({ fadeIn: (0).toFixed(2) });

        this.timelineError.showAndHide('Fade-in value must be a positive number');
      }

      this.fadeInElement.nativeElement.value = (Number(this.selectedSegmentValue.fadeIn) / 1000).toFixed(2);
    } else {
      if (this.getSegmentType(this.selectedSegmentValue) !== SegmentType.SoundEffect && value > 0) {
        if (value >= duration - this.selectedSegmentValue.fadeIn) {
          this.selectedSegment.patchValue({ fadeOut: (duration - this.selectedSegmentValue.fadeIn).toFixed(2) });

          this.timelineError.showAndHide('Fade-out value is more than the segment\'s duration');
        } else {
          this.selectedSegment.patchValue({ fadeOut: value.toFixed(2) });
        }
      } else {
        this.selectedSegment.patchValue({ fadeOut: (0).toFixed(2) });

        this.timelineError.showAndHide('Fade-out value must be a positive number');
      }

      this.fadeOutElement.nativeElement.value = (Number(this.selectedSegmentValue.fadeOut) / 1000).toFixed(2);
    }
  }

  updateLightShowSize() {
    let lightShowSizeBytes = 0;
    this.imageLibrary.forEach(libImage => {
      const segments = this.segments.filter(s => s.imageName === libImage.name);
      if (segments.length > 0) {
        lightShowSizeBytes += libImage.size ? libImage.size : 0;
      }
    });

    const percent = lightShowSizeBytes / (20 * 1024 * 1024);

    if (percent < .5) {
      this.lightShowSizeColor = `rgba(${510 * percent}, 255, 0, 255)`;
    } else if (percent === .5) {
      this.lightShowSizeColor = `rgba(255, 255, 0, 255)`;
    } else if (percent > .5) {
      this.lightShowSizeColor = `rgba(255, ${510 * (1 - percent)}, 0, 255)`;
    }

    this.lightShowSize = Math.floor(lightShowSizeBytes / (1024 * 1024));

  }

  updateFadeInAndFadeOut() {

    this.updateLightShowSize();

    if (this.selectedSegmentValue && +this.selectedSegmentValue.fadeIn + +this.selectedSegmentValue.fadeOut > this.selectedSegmentValue.duration) {
      this.selectedSegment.patchValue({ fadeIn: (0).toFixed(2) });
      this.selectedSegment.patchValue({ fadeOut: (0).toFixed(2) });

      this.fadeInElement.nativeElement.value = (Number(this.selectedSegmentValue.fadeIn) / 1000).toFixed(2);
      this.fadeOutElement.nativeElement.value = (Number(this.selectedSegmentValue.fadeOut) / 1000).toFixed(2);
    } else if (this.selectedSegmentValue && this.fadeInElement && this.fadeOutElement) {
      this.fadeInElement.nativeElement.value = (Number(this.selectedSegmentValue.fadeIn) / 1000).toFixed(2);
      this.fadeOutElement.nativeElement.value = (Number(this.selectedSegmentValue.fadeOut) / 1000).toFixed(2);
    }
  }

  addSegment() {
    const filteredSegments = this.segments.filter(seg => this.getSegmentType(seg) === SegmentType.SolidColor);

    const initialPositionAtCursor = this.state.playbackPosition;

    const trackDurationMs = moment.duration(this.track.duration).asMilliseconds();

    const cutOff = trackDurationMs - (this.cropEndPercent / 100) * trackDurationMs;

    const incompatibleElement = filteredSegments.find(seg => (
      (seg.position >= initialPositionAtCursor &&
        seg.position <= initialPositionAtCursor + 5000) ||
      (seg.position + seg.duration >= initialPositionAtCursor &&
        seg.position + seg.duration <= initialPositionAtCursor + 5000)));

    if (!incompatibleElement && (cutOff - initialPositionAtCursor >= 5000)) {
      this.addSegmentObjectToFormGroup();
    } else if (this.segments.length) {
      filteredSegments.sort((a, b) => a.position - b.position);

      const availableSpace = [];

      for (let i = 0; i < filteredSegments.length - 1; i++) {
        if (filteredSegments[i + 1].position - (filteredSegments[i].position + filteredSegments[i].duration) >= this.DEFAULT_SEGMENT_LENGTH) {
          availableSpace.push(filteredSegments[i].position + filteredSegments[i].duration);
        }
      }

      if ((!filteredSegments.length && this.DEFAULT_SEGMENT_LENGTH < cutOff) || (filteredSegments.length && filteredSegments[0].position >= this.DEFAULT_SEGMENT_LENGTH)) {
        availableSpace.unshift(0);
      }
      if (filteredSegments.length && cutOff - (filteredSegments[filteredSegments.length - 1].position + filteredSegments[filteredSegments.length - 1].duration) >= this.DEFAULT_SEGMENT_LENGTH) {
        availableSpace.push(filteredSegments[filteredSegments.length - 1].position + filteredSegments[filteredSegments.length - 1].duration);
      }

      if (availableSpace.length === 0) {
        this.timelineError.showAndHide('No space to add a color segment.');
      } else {
        let index = 0;
        availableSpace.forEach((num, i) => {
          if (initialPositionAtCursor - num < initialPositionAtCursor - availableSpace[index]) {
            index = i;
          }
        });

        this.addSegmentObjectToFormGroup(availableSpace[index] + 1);
      }
    }

    this.sortSegments();
    setTimeout(() => this.scrollTimeline(), 0);

    this.updateLightShowSize();
  }

  addTorchSegment(){
    this.torchEditor.addSelectedOperation();
  }

  addSegmentObjectToFormGroup(position?: number) {

    const newSegmentObject = {
      colors: ['#FFFFFF'],
      position: position ? position : this.state.playbackPosition,
      duration: this.DEFAULT_SEGMENT_LENGTH,
      minDuration: 100,
      segmentType: LightshowSegmentTypes.Unknown,
      imageUrl: '',
      imageName: '',
      imageId: '',
      audioUrl: '',
      audioName: '',
      fadeIn: 0,
      fadeOut: 0,
      hidden: false
    } as BlankSegment;

    //Object.setPrototypeOf(newSegmentObject, ToggleSolidColorSegment.prototype);
    Object.setPrototypeOf(newSegmentObject, BlankSegment.prototype);

    const newSegmentGroup = LightshowPreviewComponent.segmentToGroup(this.fb, <any>newSegmentObject);

    const fa = this.form.get('customModeData.segments') as FormArray;
    fa.push(newSegmentGroup);

    this.form.get('customModeData').patchValue({ selectedSegmentIndex: fa.length - 1 });
    this.segmentSelected({ index: fa.length - 1 });
  }

  setSegmentType(type: number) {
    if (type !== this.selectedSegment.value.segmentType) {
      this.selectedSegment.patchValue({ segmentType: type });
    }
  }

  segmentSelected(data: SegmentSelectedData) {
    if (data.index !== -1) {
      this.selectedSegment = (this.form.get('customModeData.segments') as FormArray).controls[data.index] as FormGroup;
      this.updateFadeInAndFadeOut();
    }
  }

  onEnterColor(type: 'backgroundColorString' | 'editColorString' | 'colorString', colorValue: string) {
    switch (type) {
      case 'colorString':
        this.colorString.nativeElement.value = colorValue ? colorValue : '#';
        this.invalidColorString = false;
        break;
      case 'editColorString':
        this.editColorString.nativeElement.value = colorValue ? colorValue : '#';
        this.invalidEditColorString = false;
        break;
      case 'backgroundColorString':
        this.backgroundColorString.nativeElement.value = colorValue ? colorValue : '#';
        this.invalidBackgroundColorString = false;
        break;
    }
  }

  public saveCurrentLightShowProgress() {
    this.saveLightShow.emit();
  }

  onSaveColor(type: 'backgroundColorString' | 'editColorString' | 'colorString', colorValue: string): boolean {
    const colorCodeValidator = /^#([A-Fa-f0-9]{6})$/;
    const invalidColorCode = !colorCodeValidator.test(colorValue) && colorValue.length && colorValue !== '#';

    switch (type) {
      case 'colorString':
        this.invalidColorString = invalidColorCode;
        break;
      case 'editColorString':
        this.invalidEditColorString = invalidColorCode;
        break;
      case 'backgroundColorString':
        this.invalidBackgroundColorString = invalidColorCode;
        break;
    }

    if (!invalidColorCode && colorValue.length && colorValue !== '#') {
      switch (type) {
        case 'colorString':
          this.colorString.nativeElement.value = colorValue;
          break;
        case 'editColorString':
          this.editColorString.nativeElement.value = colorValue;
          break;
        case 'backgroundColorString':
          this.backgroundColorString.nativeElement.value = colorValue;
          break;
      }
    }

    if (invalidColorCode) {
      return false;
    }
    return true;
  }

  preventAutoPlay(e) {
    if (e.charCode === 13) {
      e.preventDefault();
      e.target.blur();
    }
  }

  onSegmentDropped(event) {

    if (!event || !event?.data) {

      return;
    }

    const droppedPosition = Math.min(event.croppedDuration, (event.offsetX / event.grooveWidth * event.duration) / event.zoomScale);

    const originalPosition = this.state.playbackPosition;

    if (event.duration - droppedPosition >= this.DEFAULT_SEGMENT_LENGTH) {

      this.seekPosition(droppedPosition);

      this.addSegment();

      const data = event.data;

      switch(data.type) {

        case 'image':

          const image = this.imageLibrary[data.objectIndex];
          this.setSegmentImage(image);
          break;

        case 'color':

          const color = this.colorsLibrary[data.objectIndex];
          this.setSegmentColor(color.colors ?? [color.color], color.type === 'strobe');
          break;

        case 'sound':

          const sound = this.soundEffects[data.objectIndex];
          this.setSegmentSoundEffect(sound);
          break;

        default:
          break;
      }

    } else {

      this.timelineError.showAndHide("Not enough room to place segment");
    }

    this.seekPosition(originalPosition)
  }

  private sortSegments() {

    if (this.segments && this.segments.length) {
      const uniqueId = this.selectedSegment.value.uniqueid;

      const segmentsFA = (this.form.get('customModeData.segments') as FormArray);

      segmentsFA.controls.sort((a, b) => a.value.position - b.value.position);

      segmentsFA.updateValueAndValidity();

      const segmentToSelect = this.segments.find(s => s.uniqueid === uniqueId);

      this.segmentSelected({
        index: this.segments.indexOf(segmentToSelect)
      });
    }
  }

  private loadTrackData() {
    this.mp3Url = this.track.trackUrl;
    this.originalTrackOperations = [];

    if (!this.form) {
      return;
    }

    // if asset data is found, use light show data from there, otherwise use the default track lightshow
    const assetLightShowJson = this.form.get('lightShowJson');
    let operationsData = [];
    if (!this.selectTrack && assetLightShowJson && assetLightShowJson.value !== '[]' && assetLightShowJson.value !== '') {
      operationsData = JSON.parse(assetLightShowJson.value);
    } else if (this.track.lightShow) {
      operationsData = JSON.parse(this.track.lightShow);
    }

    // parse the lightshow json into operations
    operationsData.forEach((element, index) => {
      // ignore overlap if two elements are overlapping when they are parsed
      // this usually means the lightshow originated on Dash, which allows overlapping operations
      const ignoreOverlap = this.checkForOverlappingOperations(operationsData, element, index);

      switch (+element.type) {
        case FlashTypes.torchOn:
          this.originalTrackOperations.push(new TorchOperation(+element.time, element.duration * 1000, element.sectionList, ignoreOverlap));
        break;
        case FlashTypes.strobeOn:
          this.originalTrackOperations.push(new StrobeOperation(+element.time, element.duration * 1000, element.sectionList, ignoreOverlap));
        break;
        case FlashTypes.sparkleSteady:
        case FlashTypes.sparkleRampUp:
        case FlashTypes.sparkleRampDown:
          this.originalTrackOperations.push(new SparkleOperation(+element.type, +element.time, element.duration * 1000, element.sectionList, ignoreOverlap));
        break;
      }
    });

    this.trackOperations = this.originalTrackOperations;

  }

  private checkForOverlappingOperations(operationsData: any[], currentTorch: any, currentIndex: number) {
    let ignoreOverlap = false;
    operationsData.forEach((op, index) => {
      if (currentIndex !== index && (
        // Case #1 - Current Torch overlapps with right side of torch
        (+currentTorch.time < (+op.time + +op.duration * 1000) && (+currentTorch.time > +op.time)) ||
        // Case #2 - Current Torch overlapps with left side of torch
        (((+currentTorch.time + +currentTorch.duration * 1000) < (+op.time + +op.duration * 1000)) && ((+currentTorch.time + +currentTorch.duration * 1000) > +op.time)) ||
        // Case #3 - Current Torch completely overlapps a torch
        ((+currentTorch.time < +op.time) && ((+currentTorch.time + +currentTorch.duration * 1000) > (+op.time + +op.duration * 1000))) ||
        // Case #4 - Current Torch is completely within a torch
        ((+currentTorch.time > +op.time) && (+currentTorch.time + +currentTorch.duration * 1000) < (+op.time + +op.duration * 1000)))) {
        ignoreOverlap = true;
      }
    });
    return ignoreOverlap;
  }

  private combineOperations(): LightShowOperation[] {
    let operations = [];
    // add track operations
    operations = operations.concat(this.trackOperations);
    // add user defined operations

    this.segments.filter(seg => !seg.hidden).forEach(s => LightshowPreviewComponent.initSegment(s).addOperations(operations));
    return operations.sort((o1, o2) => o1.position - o2.position);
  }

  /**
  *  This function cleans up segments that are outside of the track length when swtiching different tracks.
  */
  private cleanSegments() {
    if (this.segments.length) {
      const segmentsFormArray = this.form.get('customModeData.segments') as FormArray;

      const durationChanged$ = this.player.durationChanged.subscribe(totalTrackDuration => {
        this.segments.forEach(seg => {
          if (seg.position + seg.duration > totalTrackDuration) {
            const indexToDelete = segmentsFormArray.value.indexOf(seg);
            segmentsFormArray.removeAt(indexToDelete);
          }
        });

        durationChanged$.unsubscribe();
      });
    }
  }

  private removeDropZoneEventListeners() {
    if (this.dropZone) {
      this.dropZoneEventListerners.forEach(eventName => {
        this.dropZone.removeEventListener(eventName, this.preventDefaults, false);
      });

      ['dragenter', 'dragover'].forEach(eventName => {
        this.dropZone.removeEventListener(eventName, this.highlight, false);
      });

      ['dragleave', 'drop'].forEach(eventName => {
        this.dropZone.removeEventListener(eventName, this.unHighlight, false);
      });

      this.dropZone.removeEventListener('drop', this.handleDrop, false);
    }
  }

  loadCanvas() {
    const canvas = this.venueRef.nativeElement;
    const rect = canvas.getBoundingClientRect();

    // scale the canvas to 2x the size of the image
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;

    this.updateCanvas();
  }

  selectSection(section: VenueSection) {
    this.deselectMode = false;

    section.selected = !section.selected;

    if (this.selectedGroup !== '') {
      if (section.selected) {
        section.groups.push(this.selectedGroup);
      } else {
        const i = section.groups.indexOf(this.selectedGroup);
        if (i >= 0) {
          section.groups.splice(i, 1);
        }
      }
    }

    // add or remove section from flashes, need to handle multiple selected flashes

    this.lightshowService.selectedOperations.forEach(ou => {
        const selectedOperation = this.trackOperations.find(o => ou === o.uniqueId);
        const selectedOperationIndex = this.trackOperations.indexOf(selectedOperation);
        if (section.selected && selectedOperationIndex >= 0) {
          this.trackOperations[selectedOperationIndex].sectionList.push(section.name);
          const u = new Set(this.trackOperations[selectedOperationIndex].sectionList);
          this.trackOperations[selectedOperationIndex].sectionList = Array.from(u);
        } else {
          if (selectedOperation && selectedOperation.sectionList) {
            const selectedSectionIndex = selectedOperation.sectionList.indexOf(section.name);
            if (selectedSectionIndex >= 0) {
              this.trackOperations[selectedOperationIndex].sectionList.splice(selectedSectionIndex, 1);
            } else if (!selectedOperation.sectionList || selectedOperation.sectionList.length === 0) {
              this.venue.venueSections.forEach(s => {
                if (s.name !== section.name) {
                  this.trackOperations[selectedOperationIndex].sectionList.push(s.name);
                }
              });
            }
          } else {
            selectedOperation.sectionList = this.venue.venueSections.map(s => s.name);
            selectedOperation.sectionList.splice(selectedOperation.sectionList.indexOf(section.name), 1);
          }
        }
    });

    this.updateCanvas();
    this.updateLightshowJson();
  }

  deselectSections() {
    this.venue.venueSections.map(s => s.selected = false);
    this.lightshowService.selectedOperations.forEach(o => {
      const i = this.trackOperations.indexOf(this.trackOperations.find(t => t.uniqueId === o));
      this.trackOperations[i].sectionList = [];
      this.venue.venueSections.forEach(v => {
        const n = v.groups.indexOf(this.selectedGroup);
        if (n >= 0) {
          v.groups.splice(n, 1);
        }
      });
    });
    this.deselectMode = true;
    this.updateCanvas();
    this.updateLightshowJson();
  }

  // HERE
  selectGroup(group: string) {
    this.deselectMode = false;
    if (this.selectedGroup === group) {
      this.selectedGroup = '';
      this.venue.venueSections.map(s => s.selected = false);

      // for all selected operations, remove the deselected group from their section list
      this.lightshowService.selectedOperations.forEach(o => {
        const i = this.trackOperations.indexOf(this.trackOperations.find(t => t.uniqueId === o));

        const g = this.trackOperations[i].sectionList.indexOf(group);
        if (g >= 0) {
          this.trackOperations[i].sectionList.splice(g, 1);
        }

      });
    } else {
      this.selectedGroup = group;

      this.venue.venueSections.map(s => s.selected = s.groups.indexOf(group) >= 0);
      this.lightshowService.selectedOperations.forEach(o => {
        const i = this.trackOperations.indexOf(this.trackOperations.find(t => t.uniqueId === o));
        this.trackOperations[i].sectionList = [];
        // this.trackOperations[i].sections = this.venue.venueSections.filter(v => v.selected).map(v => v.name);

        // add the group name to the track operation in place of adding all of the sections
        this.trackOperations[i].sectionList.push(group);
      });
    }

    // add or remove section from flash

    this.updateCanvas();
    this.updateLightshowJson();
  }

  // HERE
  createNewGroup() {
    if (this.newGroupName === null || this.newGroupName.match(/^ *$/) !== null) {
      return;
    }
    if (this.venue.venueGroups.indexOf(this.newGroupName) < 0) {
      this.venue.venueGroups.push(this.newGroupName);
      this.venue.venueSections.filter(s => s.selected).map(s => s.groups.push(this.newGroupName));
    }
    this.selectGroup(this.newGroupName);
    this.newGroupName = '';
    this.venue.venueGroups.sort((a, b) => a.localeCompare(b));
  }

  deleteSelectedGroup() {
    if (this.selectedGroup !== '') {
      this.venue.venueGroups.splice(this.venue.venueGroups.indexOf(this.selectedGroup), 1);
      this.venue.venueSections.forEach(s => {
        s.groups = s.groups.filter(g => g !== this.selectedGroup);
        s.selected = false;
      });
      this.selectedGroup = '';
    }
  }

  public updateCanvas() {
    // return if page isn't loaded
    if (!this.venueRef) {
      return;
    }

    // get the canvas object and context
    const canvas = this.venueRef.nativeElement;
    const ctx = canvas.getContext('2d');

    // get the canvas width and height and adjust its scale
    // based on the canvas size this venue was created with
    const w = canvas.width;
    const h = canvas.height;

    let scaleAdj = 1;
    if (this.venue.canvasWidth > 0) {
      scaleAdj = w / this.venue.canvasWidth;
    }

    // clear the entire canvas
    ctx.clearRect(0, 0, w, h);

    // if there are selected operations, draw those instead of the operations under the cursor
    let currentOperations = [];
    if (this.lightshowService.selectedOperations.length > 0) {
      currentOperations = this.trackOperations.filter(o => this.lightshowService.selectedOperations.indexOf(o.uniqueId) >= 0);
    } else {
      currentOperations = this.trackOperations ?
      this.trackOperations.filter(op => op.position <= this.state.playbackPosition && this.state.playbackPosition <= op.position + op.duration) : null;
    }

    // deselect sections and groups between operations
    if ((this.state.isPlaying && currentOperations.length === 0)) {
      this.venue.venueSections.map(s => s.selected = false);
      this.selectedGroup = '';
    }

    // if the current operations contain a group name, get all sections
    // that group is associtated with and push them to filterSections
    currentOperations.forEach(op => {
      let filterSections = [];
      if (op.sectionList && op.sectionList.length > 0) {
        const operationGroups = this.venue.venueGroups.filter(item => op.sectionList.indexOf(item) >= 0);
        if (operationGroups.length > 0) {
          const groupSections = this.venue.venueSections.filter(v => v.groups ? v.groups.indexOf(operationGroups[0]) >= 0 : false);
          this.selectedGroup = operationGroups[0];
          filterSections = groupSections.map(g => g.name);
        } else {
          filterSections = op.sectionList;
        }
      }

      // handle empty section lists, on mobile apps empty is the same as all sections
      this.venue.venueSections.map(s => s.selected = false);
      if (filterSections.length === 0 && !this.deselectMode) {
        this.venue.venueSections.map(s => s.selected = true);
      } else {
        this.venue.venueSections.map(s => filterSections.map(f => {
          if (f === s.name) {
            s.selected = true;
          }
        }));
      }

      // draw each venue section to the canvas
      this.venue.venueSections.forEach(s => {

        // begin drawing
        const region = new Path2D();
        ctx.beginPath();

        // set line width and color based on operation type
        // and which sections are selected
        if ((s.selected && filterSections.indexOf(s.name) >= 0) || filterSections.length === 0) {
          ctx.lineWidth = 8;
          ctx.strokeStyle = op.timelineColor;
          ctx.fillStyle = op.timelineColor;
        } else {
          return;
        }

        // handle the sparkle effect
        let sparkleSkip = true;
        if (this.state.isPlaying) {
          sparkleSkip = op.flashType === FlashTypes.sparkleSteady && Math.random() > .2;
        }
        const sd = s.shapeData;
        switch (s.shapeType) {
          case('polygon'):
            sd.forEach(p => {
              if (op.operationFlashOn && s.selected && !sparkleSkip) {
                region.lineTo(p.x * scaleAdj, p.y * scaleAdj);
                ctx.fill(region);
              }
              ctx.lineTo(p.x * scaleAdj, p.y * scaleAdj);
            });
          break;
          case('rectangle'):
            if (sd.length >= 2) {
              sd.length = 2;
              if (op.operationFlashOn && s.selected && !sparkleSkip) {
                region.rect(sd[0].x * scaleAdj, sd[0].y * scaleAdj, (sd[1].x - sd[0].x) * scaleAdj, (sd[1].y - sd[0].y) * scaleAdj);
                ctx.fill(region);
              }
              ctx.rect(sd[0].x * scaleAdj, sd[0].y * scaleAdj, (sd[1].x - sd[0].x) * scaleAdj, (sd[1].y - sd[0].y) * scaleAdj);
            }
          break;
          case('circle'):
            if (sd.length >= 2) {
              sd.length = 2;
              const r =  (sd[1].x - sd[0].x) / 2;
              const cx = sd[0].x + r;
              const cy = sd[0].y + r;
              if (op.operationFlashOn && s.selected && !sparkleSkip) {
                region.arc(cx * scaleAdj, cy * scaleAdj, r * scaleAdj, 0, 2 * Math.PI);
                ctx.fill(region);
              }
              ctx.arc(cx * scaleAdj, cy * scaleAdj, r * scaleAdj, 0, 2 * Math.PI);
            }
          break;
        }

        // close the shape
        region.closePath();
        ctx.closePath();
        ctx.stroke();
      });
    });
  }

  clearAllSegments(){
    this.dialogService.confirm("Are you sure you want to do that?", "This will delete all segments for the currently selected mode. If you change your mind, you can exit without saving. Therefore, it is advised you save your work before doing this.",
    this.viewRef)
      .then((result) => {
        if (result){
          if (this.editorMode === EditorMode.TORCH){
            this.torchEditor.lightshowOperations.splice(0, this.torchEditor.lightshowOperations.length);
            this.lightshowService.operationArrayChange$.next();
          } else {
            var screenSegments = this.form.get('customModeData.segments') as FormArray;
            screenSegments.clear();
          }
          this.form.markAsDirty();
        }
      })
  }

  ngOnDestroy() {
    this.state.isPlaying = false;

    this._ngDestroy$.complete();

    if (this._onDestroy$) {
      this._onDestroy$.next();
      this._onDestroy$.complete();
    }

    this.cleanSegments();
    this.removeDropZoneEventListeners();
  }
}
